import React, { useEffect, useMemo, useState } from 'react';
import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { auth, db } from '../../firebase/firebase';
import { Bell, Clock, Gavel, Plus, TrendingUp } from 'lucide-react';
import ImageUploader from '../../components/ImageKit/PlantImage';
import './BiddingPage.css';

const defaultAuction = {
  productName: '',
  description: '',
  startingBid: '',
  minIncrement: '10',
  endAt: '',
  imageUrl: '',
};

const BiddingPage = () => {
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [auctionForm, setAuctionForm] = useState(defaultAuction);
  const [bidValues, setBidValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const currentUser = auth.currentUser;

  useEffect(() => {
    const auctionsQuery = query(collection(db, 'auctions'), orderBy('endAt', 'asc'));
    const unsubscribe = onSnapshot(auctionsQuery, (snapshot) => {
      const rows = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
      setAuctions(rows);
      setLoading(false);
    }, (error) => {
      console.error('Failed to load auctions:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const activeAuctions = useMemo(() => {
    const now = Date.now();
    return auctions.map((auction) => ({
      ...auction,
      isEnded: auction.endAt?.toMillis ? auction.endAt.toMillis() <= now : false,
    }));
  }, [auctions]);

  const handleCreateAuction = async (event) => {
    event.preventDefault();
    if (!currentUser) {
      alert('Please sign in to create an auction.');
      return;
    }

    const startingBid = Number(auctionForm.startingBid);
    const minIncrement = Number(auctionForm.minIncrement);
    const endDate = new Date(auctionForm.endAt);

    if (!auctionForm.productName.trim() || !startingBid || startingBid <= 0 || !minIncrement || minIncrement <= 0 || Number.isNaN(endDate.getTime()) || endDate <= new Date()) {
      alert('Please enter a valid product, bid amount, increment, and future end time.');
      return;
    }

    try {
      setSubmitting(true);
      await addDoc(collection(db, 'auctions'), {
        productName: auctionForm.productName.trim(),
        description: auctionForm.description.trim(),
        startingBid,
        minIncrement,
        currentBid: startingBid,
        bidCount: 0,
        sellerId: currentUser.uid,
        sellerName: currentUser.displayName || currentUser.email || 'Seller',
        imageUrl: auctionForm.imageUrl || '',
        images: auctionForm.imageUrl ? [auctionForm.imageUrl] : [],
        status: 'active',
        endAt: Timestamp.fromDate(endDate),
        createdAt: serverTimestamp(),
        winnerId: null,
      });

      setAuctionForm(defaultAuction);
      setShowCreate(false);
    } catch (error) {
      console.error('Auction creation failed:', error);
      alert('Could not create auction. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const placeBid = async (auction) => {
    if (!currentUser) {
      alert('Please sign in to bid.');
      return;
    }
    if (auction.sellerId === currentUser.uid) {
      alert('You cannot bid on your own auction.');
      return;
    }
    if (auction.isEnded || auction.status !== 'active') {
      alert('This auction has ended.');
      return;
    }

    const bidAmount = Number(bidValues[auction.id]);
    const minimumBid = Number(auction.currentBid || auction.startingBid || 0) + Number(auction.minIncrement || 1);
    if (!bidAmount || bidAmount < minimumBid) {
      alert(`Minimum bid is ₹${minimumBid.toLocaleString('en-IN')}.`);
      return;
    }

    try {
      const auctionRef = doc(db, 'auctions', auction.id);
      await runTransaction(db, async (transaction) => {
        const auctionSnap = await transaction.get(auctionRef);
        if (!auctionSnap.exists()) throw new Error('Auction no longer exists.');

        const data = auctionSnap.data();
        const latestMinimum = Number(data.currentBid || data.startingBid || 0) + Number(data.minIncrement || 1);
        const latestEnd = data.endAt?.toMillis ? data.endAt.toMillis() : 0;
        if (data.status !== 'active' || latestEnd <= Date.now()) throw new Error('Auction has ended.');
        if (bidAmount < latestMinimum) throw new Error(`Minimum bid is ₹${latestMinimum.toLocaleString('en-IN')}.`);

        const bidRef = doc(collection(db, 'auctions', auction.id, 'bids'));
        transaction.set(bidRef, {
          bidderId: currentUser.uid,
          bidderName: currentUser.displayName || currentUser.email || 'Bidder',
          amount: bidAmount,
          createdAt: serverTimestamp(),
        });
        transaction.update(auctionRef, {
          currentBid: bidAmount,
          highestBidderId: currentUser.uid,
          highestBidderName: currentUser.displayName || currentUser.email || 'Bidder',
          bidCount: Number(data.bidCount || 0) + 1,
          updatedAt: serverTimestamp(),
        });
      });

      await addDoc(collection(db, 'notifications'), {
        userId: auction.sellerId,
        type: 'bid',
        title: 'New bid received',
        message: `${currentUser.displayName || currentUser.email || 'A buyer'} bid ₹${bidAmount.toLocaleString('en-IN')} on ${auction.productName}.`,
        fromUserId: currentUser.uid,
        read: false,
        timestamp: serverTimestamp(),
      });

      setBidValues((current) => ({ ...current, [auction.id]: '' }));
    } catch (error) {
      console.error('Bid failed:', error);
      alert(error.message || 'Could not place bid.');
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp?.toDate) return 'No end time';
    return timestamp.toDate().toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <main className="bidding-shell">
      <section className="bidding-hero">
        <div>
          <span><Gavel size={16} /> Bidding Market</span>
          <h1>Live plant auctions for serious buyers.</h1>
          <p>Sellers can open time-bound auctions. Buyers place transparent bids inside Ankurit, with real-time updates and in-app notifications.</p>
        </div>
        <button className="bidding-primary" onClick={() => setShowCreate((current) => !current)}>
          <Plus size={18} />
          {showCreate ? 'Close form' : 'Create auction'}
        </button>
      </section>

      {showCreate && (
        <form className="auction-form" onSubmit={handleCreateAuction}>
          <h2>Create auction</h2>
          <div className="auction-form-grid">
            <label>
              Product name
              <input value={auctionForm.productName} onChange={(event) => setAuctionForm((current) => ({ ...current, productName: event.target.value }))} required />
            </label>
            <label>
              Starting bid
              <input type="number" min="1" value={auctionForm.startingBid} onChange={(event) => setAuctionForm((current) => ({ ...current, startingBid: event.target.value }))} required />
            </label>
            <label>
              Minimum increment
              <input type="number" min="1" value={auctionForm.minIncrement} onChange={(event) => setAuctionForm((current) => ({ ...current, minIncrement: event.target.value }))} required />
            </label>
            <label>
              End time
              <input type="datetime-local" value={auctionForm.endAt} onChange={(event) => setAuctionForm((current) => ({ ...current, endAt: event.target.value }))} required />
            </label>
          </div>
          <label>
            Description
            <textarea rows="3" value={auctionForm.description} onChange={(event) => setAuctionForm((current) => ({ ...current, description: event.target.value }))} />
          </label>
          <div className="auction-upload-field">
            <span>Auction photo</span>
            <ImageUploader
              userId={currentUser?.uid}
              type="auction"
              currentImageUrl={auctionForm.imageUrl}
              onUploadComplete={(url) => setAuctionForm((current) => ({ ...current, imageUrl: url }))}
              label="Upload auction photo"
            />
          </div>
          <button type="submit" className="bidding-primary" disabled={submitting}>{submitting ? 'Creating...' : 'Publish auction'}</button>
        </form>
      )}

      <section className="auction-list">
        {loading ? (
          <div className="auction-empty">Loading auctions...</div>
        ) : activeAuctions.length === 0 ? (
          <div className="auction-empty">No auctions yet.</div>
        ) : (
          activeAuctions.map((auction) => {
            const minimumBid = Number(auction.currentBid || auction.startingBid || 0) + Number(auction.minIncrement || 1);

            return (
              <article className={`auction-card ${auction.isEnded ? 'ended' : ''}`} key={auction.id}>
                {auction.imageUrl && (
                  <div className="auction-image">
                    <img src={auction.imageUrl} alt={auction.productName || 'Auction plant'} />
                  </div>
                )}
                <div className="auction-card-header">
                  <span className="auction-status">{auction.isEnded ? 'Ended' : auction.status || 'active'}</span>
                  <span><Clock size={14} /> Ends {formatTime(auction.endAt)}</span>
                </div>
                <h2>{auction.productName}</h2>
                {auction.description && <p>{auction.description}</p>}
                <div className="auction-metrics">
                  <div>
                    <span>Current bid</span>
                    <strong>₹{Number(auction.currentBid || auction.startingBid || 0).toLocaleString('en-IN')}</strong>
                  </div>
                  <div>
                    <span>Bids</span>
                    <strong>{auction.bidCount || 0}</strong>
                  </div>
                  <div>
                    <span>Increment</span>
                    <strong>₹{Number(auction.minIncrement || 0).toLocaleString('en-IN')}</strong>
                  </div>
                </div>
                {auction.highestBidderName && (
                  <div className="auction-leading"><TrendingUp size={16} /> Leading: {auction.highestBidderName}</div>
                )}
                <div className="bid-row">
                  <input
                    type="number"
                    min={minimumBid}
                    placeholder={`Min ₹${minimumBid.toLocaleString('en-IN')}`}
                    value={bidValues[auction.id] || ''}
                    onChange={(event) => setBidValues((current) => ({ ...current, [auction.id]: event.target.value }))}
                    disabled={auction.isEnded || auction.sellerId === currentUser?.uid}
                  />
                  <button onClick={() => placeBid(auction)} disabled={auction.isEnded || auction.sellerId === currentUser?.uid}>
                    <Bell size={16} />
                    Bid
                  </button>
                </div>
              </article>
            );
          })
        )}
      </section>
    </main>
  );
};

export default BiddingPage;
